import {
  DateSelectArg,
  DateSpanApi,
  EventApi,
  EventClickArg,
  EventDropArg,
  EventInput,
} from "@fullcalendar/core";
import { EventResizeStopArg } from "@fullcalendar/interaction";
import * as React from "react";
import { CalendarType } from "../../../components/atoms/Calendar";
import { truncateName } from "../../../domains/LearningMaterial";
import { changeStartDate as changeStartDateRecurrence } from "../../../domains/Recurrence";
import {
  create,
  defaultScheduleTitle,
  Schedule,
  ScheduleForStudent,
  validateDateRange,
} from "../../../domains/Schedule";
import TimeHelper from "../../../helpers/TimeHelper";
import { useQueryError } from "../../../hooks/http/useQueryError";
import StudentInterface from "../../../interfaces/StudentInterface";
import styleVars from "../../../styles/variables.scss";
import { useFetchSchedules } from "./useScheduleApi";
import { determineScheduleColorCode } from "./utilities";

export type UseStudentCalendarContainerProps = {
  onMutateError: (message: string) => void;
  onMutateSuccess: (message: string) => void;
  onSelectSchedule: (schedule: ScheduleForStudent | null) => void;
  student: StudentInterface;
  startDate: Date;
  endDate: Date;
  calendarType: CalendarType;
  onSelectNewScheduleFromSlot: (schedule?: Schedule) => void;
  openConfirmModal: (schedule: ScheduleForStudent) => void;
  openConfirmModalSingle: (schedule: ScheduleForStudent) => void;
};

export const useStudentCalendarContainer = (
  props: UseStudentCalendarContainerProps,
) => {
  const { isLoading, data, error } = useFetchSchedules({
    from: props.startDate,
    to: props.endDate,
    studentId: props.student.id,
  });
  useQueryError(error);

  const events = React.useMemo(
    () => (data ? buildEventsFromSchedules(data) : []),
    [data],
  );

  const onChangeScheduleFromInteraction = React.useCallback(
    (arg: EventDropArg | EventResizeStopArg) => {
      const schedule = arg.event.extendedProps.schedule as ScheduleForStudent;
      const newStart = arg.event.start;
      const newEnd = arg.event.end;
      if (!newStart || !newEnd) {
        return;
      }

      const newStudyDuration = calcNewStudyDuration({
        originalSchedule: schedule,
        newScheduleStartTime: newStart,
        newScheduleEndTime: newEnd,
      });
      const newRecurrence = schedule.recurrence
        ? changeStartDateRecurrence({
            currentReucurrence: schedule.recurrence,
            startDate: newStart,
          })
        : null;

      const newSchedule: ScheduleForStudent = {
        ...schedule,
        startAt: newStart,
        endAt: newEnd,
        studySchedule: schedule.studySchedule
          ? { ...schedule.studySchedule, numberOfSeconds: newStudyDuration }
          : schedule.studySchedule,
        recurrence: newRecurrence,
      };

      // リクエスト前に新データで予定の表示を更新する
      // 確認モーダルでキャンセルされた場合は refetch により元に戻る
      arg.event.setProp("title", formatToDisplayTitleOnCalendar(newSchedule));

      if (schedule.recurrence) {
        props.openConfirmModal(newSchedule);
        return;
      }
      props.openConfirmModalSingle(newSchedule);
    },
    [],
  );

  const onSelectFromSlot = React.useCallback((arg: DateSelectArg) => {
    props.onSelectNewScheduleFromSlot(
      create({ startAt: arg.start, endAt: arg.end, allday: arg.allDay }),
    );
  }, []);

  const onClickSchedule = React.useCallback((e: EventClickArg) => {
    props.onSelectSchedule(
      e.event.extendedProps.schedule as ScheduleForStudent,
    );
  }, []);

  const shouldShowPlaceHolderEvent = React.useCallback(
    (dropInfo: DateSpanApi, draggedEvent: EventApi | null) => {
      if (
        validateDateRange(
          create({
            startAt: dropInfo.start,
            endAt: dropInfo.end,
            allday: dropInfo.allDay,
          }),
        ) !== "valid"
      ) {
        return false;
      }

      // 移動元がない(予定の新規作成の)場合はtrueを返す
      if (!draggedEvent) {
        return true;
      }

      // 終日予定から日時予定には変更できない
      // 日時予定から終日予定には変更できない
      return dropInfo.allDay === draggedEvent.allDay;
    },
    [],
  );

  return {
    onChangeScheduleFromInteraction,
    onSelectFromSlot,
    events,
    isLoading,
    error,
    eventColor: styleVars.colorPrimary,
    onClickSchedule,
    shouldShowPlaceHolderEvent,
  };
};

// build fullcalendar's event objects
// ref: https://fullcalendar.io/docs/event-object
const buildEventsFromSchedules = (
  schedules: readonly ScheduleForStudent[],
): EventInput[] => {
  return schedules.map((schedule: ScheduleForStudent) => {
    const eventAttributes = buildEventAttributesFromSchedule(schedule);

    return {
      ...eventAttributes,
    };
  });
};

type CalcNewStudyDurationParam = {
  originalSchedule: Schedule;
  newScheduleStartTime: Date;
  newScheduleEndTime: Date;
};
const calcNewStudyDuration = ({
  originalSchedule: schedule,
  newScheduleEndTime,
  newScheduleStartTime,
}: CalcNewStudyDurationParam) => {
  const scheduleDurationMs =
    schedule.endAt.getTime() - schedule.startAt.getTime();

  const studyDuration = schedule.studySchedule
    ? schedule.studySchedule.numberOfSeconds
    : 0;

  if (scheduleDurationMs === studyDuration * 1000) {
    const newStudyDurationMs =
      newScheduleEndTime.getTime() - newScheduleStartTime.getTime();
    return Math.floor(newStudyDurationMs / 1000);
  }
  return studyDuration;
};

const buildEventAttributesFromSchedule = (schedule: ScheduleForStudent) => {
  const backgroundColor = determineScheduleColorCode(schedule);

  return {
    allDay: schedule.allday,
    title: formatToDisplayTitleOnCalendar(schedule),
    start: schedule.startAt,
    end: schedule.endAt,
    backgroundColor,
    borderColor: backgroundColor,
    extendedProps: { schedule },
  };
};

const formatToDisplayTitleOnCalendar = (schedule: Schedule): string => {
  const { studySchedule } = schedule;

  let title = schedule.summary || defaultScheduleTitle;

  if (studySchedule) {
    title = schedule.summary ? `${schedule.summary}：` : "";
    title += truncateName(studySchedule.learningMaterial);

    const additionalInfo = [
      studySchedule.numberOfSeconds
        ? TimeHelper.secondsToDisplayTime(studySchedule.numberOfSeconds)
        : null,
      studySchedule.amount
        ? `${studySchedule.amount}${studySchedule.learningMaterial.unit}`
        : null,
    ].filter((info) => info !== null);

    if (additionalInfo.length > 0) {
      const additionalText = additionalInfo.join(" / ");

      title += ` / ${additionalText}`;
    }
  }

  return title;
};
